import { ImageModalFormattedText } from 'app/components/common/FormattedText';
import Modal, { ModalPriority } from 'app/components/modals/Modal';
import { DEFAULT_MEDIUM_BREAKPOINT, MINIMUM_DESIRED_CONTENT_WIDTH } from 'app/constants/Breakpoints';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import * as Schema from 'app/constants/schemas';
import { TRANSCODING_MODAL } from 'app/constants/Transcoding';
import { useTranscodedSource } from 'app/services/transcoderService/react';
import { useCss } from 'app/utils/css/index';
import React, { FunctionComponent, ReactNode } from 'react';
import X from 'app/components/svgs/X';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import useMeasure from 'app/components/layout/useMeasure';
import Draw from 'app/components/rtc/draw/Draw';

const MIN_WIDTH = MINIMUM_DESIRED_CONTENT_WIDTH;
const MIN_HEIGHT = 253;

interface IImageModalProps {
  image: Schema.Image;
  onClose: () => void;
  hasCaption?: boolean;
  caption?: ReactNode;
  drawKeyPath?: KeyPath;
}

const ImageModal: FunctionComponent<IImageModalProps> = ({
  image: { attribution, publicAttribution, dimensions, url },
  onClose,
  hasCaption = false,
  caption = '',
  drawKeyPath,
}) => {
  let maxWidth: string | undefined;
  let padding: string | undefined;

  const transcodedSrc = useTranscodedSource(url, TRANSCODING_MODAL);

  // Ensure the text never exceeds the size of the image (unless minWidth is larger).
  if (dimensions && dimensions.x) {
    maxWidth = `${dimensions.x}px`;
  }

  // Center image vertically if it is less than the minimum height.
  if (dimensions && dimensions.y) {
    const verticalPadding = Math.max(0, (MIN_HEIGHT - dimensions.y) / 2);
    padding = `${verticalPadding}px 0px`;
  }

  const [imageSize, imageRef] = useMeasure();

  const css = useCss();

  const imageModalClass = css('imageModal', {
    position: 'relative',

    // If the container can't shrink anymore, make sure it doesn't go off the page from the top.
    maxHeight: 'calc(100%)',
  });

  const imageContainerClass = css('imageContainer', {
    // If the image is really narrow or small, set a minimum width
    // as the smallest possible width before lesson content goes full screen.
    minWidth: `${MIN_WIDTH}px`,
    maxWidth: maxWidth || '70vw',
    color: COLOR_PALETTE.grey.dark,

    [`@media (max-width: ${DEFAULT_MEDIUM_BREAKPOINT}px)`]: {
      minWidth: 'calc(100vw - 4em)',
    },
  });

  const containerBackgroundClass = css('containerBackground', {
    backgroundColor: COLOR_PALETTE.grey.white,
    borderRadius: '5px',
    overflow: 'hidden',
    boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.3)',
  });

  const imageClass = css('image', {
    display: 'block',
    margin: '0 auto',

    // Takes up the entire width of the container.
    maxWidth: '100%',

    // Ensure the image doesn't take up more than 70% of the viewport height.
    maxHeight: '70vh',

    padding,
  });

  const textContainerClass = css('textContainer', {
    margin: '0.5em 1em',
    lineHeight: '1.3',
    wordWrap: 'break-word',
  });

  const drawClass = css('draw', {
    position: 'absolute',
    top: 0,
    // Use calc to avoid browser bugs with calculating bounding
    // rect offsets if we used transforms
    left: `calc(50% - ${imageSize.width / 2}px)`,
  });

  const attributionClass = css('attribution', {
    fontSize: '11px',
  });

  const captionClass = css('caption', {
    fontSize: '14px',
    fontWeight: 600,
  });

  return (
    <Modal priority={ModalPriority.LOWER} onClose={onClose} noBackground>
      <div data-testid="image-modal" className={imageModalClass}>
        <ImageModalCloseButton onClick={onClose} />

        <div data-testid="image-modal-container" className={imageContainerClass}>
          <div className={containerBackgroundClass}>
            <img alt="" className={imageClass} src={transcodedSrc} data-testid="image-modal-img" ref={imageRef} />

            {drawKeyPath && <div className={drawClass}>
              <Draw size={imageSize} keyPath={drawKeyPath} />
            </div>}

            {(hasCaption || (attribution && publicAttribution)) && (
              <div className={textContainerClass}>
                {hasCaption && (
                  <div className={captionClass}>
                    {caption}
                  </div>
                )}

                {attribution && publicAttribution &&
                  <div className={attributionClass}>
                    &copy; <ImageModalFormattedText text={attribution} />
                  </div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface IImageModalCloseButtonProps {
  onClick: () => void;
}

const ImageModalCloseButton: FunctionComponent<IImageModalCloseButtonProps> = ({ onClick }) => {
  const css = useCss();
  const closeButtonClass = css('closeButton', {
    position: 'absolute',
    right: '-0.4em',
    top: '-1.8em',
    cursor: 'pointer',
    width: '0.8em',
    height: '0.8em',
    padding: '0.5em',
    display: 'flex',
  });
  return (
    <div className={closeButtonClass} onClick={onClick} data-testid="image-modal-close-btn">
      <X color={COLOR_PALETTE.grey.white} />
    </div>
  );
};

export default ImageModal;
